// Helpers on top of `translate()` for strings that need more than plain
// `{placeholder}` substitution: React nodes inside a sentence (a bold word,
// a styled number) and one/other plurals.

import { Fragment, type ReactNode } from 'react';
import type { MessageKey } from './messages.js';

/**
 * Split a translated template on `{name}` placeholders and splice in React
 * nodes, so word order stays in the dictionary. Pass the template untouched
 * (`t(key)` with no vars leaves placeholders intact); unknown placeholders
 * are left as text.
 */
export function rich(template: string, parts: Readonly<Record<string, ReactNode>>): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    const name = match[1] ?? '';
    if (!(name in parts)) continue;
    const at = match.index ?? 0;
    if (at > last) out.push(template.slice(last, at));
    out.push(<Fragment key={`${name}-${at}`}>{parts[name]}</Fragment>);
    last = at + match[0].length;
  }
  if (last < template.length) out.push(template.slice(last));
  return <>{out}</>;
}

type PluralBase<K extends string> = K extends `${infer B}.one` ? B : never;

/** Pick the `.one` or `.other` variant of a plural key pair for `count`. */
export function plural<B extends PluralBase<MessageKey>>(
  base: B,
  count: number,
): `${B}.one` | `${B}.other` {
  return count === 1 ? `${base}.one` : `${base}.other`;
}
